import { useCallback, useEffect, useState } from "react";
import { RotateCcw, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate, formatTRY } from "@/lib/format";
import { useOffline } from "@/lib/net";

const PT = { musteri: "Müşteri", tedarikci: "Tedarikçi", her_ikisi: "Her ikisi" };

function PreviewDialog({ p, type, onClose, onApply, busy }) {
  const off = useOffline();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="party-preview-dialog">
        <DialogHeader><DialogTitle>{PT[type]} işaretleme · önizleme</DialogTitle></DialogHeader>
        <div className="text-[13px] space-y-1" data-testid="party-preview-summary">
          <div><b>{p.count}</b> cari {PT[type]} olarak işaretlenecek · <b>{p.new_suppliers}</b> yeni tedarikçi kartı açılacak</div>
          {type === "tedarikci"
            ? <div>Tedarikçi hesabına taşınacak bakiye (borcumuz): <b className="font-mono" data-testid="party-preview-total">{formatTRY(p.total_moved)}</b> · cari bakiyeleri sıfırlanır, geçmiş hareketler korunur</div>
            : <div className="text-slate-500">"Her ikisi" seçiminde bakiye cari hesapta kalır; yalnızca bağlı tedarikçi kartı açılır.</div>}
          {p.skipped.length > 0 && <div className="text-amber-700">{p.skipped.length} cari zaten bu tipte – atlanacak</div>}
        </div>
        <div className="max-h-[300px] overflow-auto border rounded">
          <table className="erp-table text-[12px]"><thead><tr><th>Kod</th><th>Unvan</th><th>Şimdiki Tip</th><th className="text-right">Taşınacak</th></tr></thead>
            <tbody>{p.rows.map((r) => <tr key={r.id}><td className="font-mono">{r.code}</td><td className="max-w-[260px] truncate">{r.title}</td><td>{PT[r.party_type]}</td>
              <td className="text-right font-mono">{r.move ? formatTRY(-r.move) : "—"}</td></tr>)}</tbody></table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="party-preview-cancel">Vazgeç</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy || off || !p.count} onClick={onApply} data-testid="party-apply-button">Onayla ve uygula</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function History({ reload, onChanged }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/party-marks/batches").then(({ data }) => setItems(data.items)); }, [reload]);
  const undo = async (b, ids = []) => {
    if (!window.confirm(ids.length ? "Bu carinin işaretlemesi geri alınsın mı?" : `${b.id} işaretlemesi tamamen geri alınsın mı?`)) return;
    try { const { data } = await api.post(`/party-marks/batches/${b.id}/rollback`, { customer_ids: ids }); toast.success(`${data.reverted} cari eski haline döndü`); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!items.length) return null;
  return (
    <div className="panel p-3 mt-4" data-testid="party-history">
      <div className="font-semibold text-[13px] mb-2">İşaretleme geçmişi</div>
      {items.map((b) => (
        <details key={b.id} className="border-t py-2 text-[12px]" data-testid={`party-batch-${b.id}`}>
          <summary className="cursor-pointer flex flex-wrap items-center gap-2">
            <span className="font-mono">{b.id}</span><span>{PT[b.party_type]}</span><span>{b.count} cari</span><span className="font-mono">{formatTRY(b.total_moved)}</span>
            <span className="text-slate-500">{formatDate(b.at)} · {b.by}</span>
            {b.status !== "tamamlandi" && <span className="text-amber-700">{b.status === "geri_alindi" ? "Geri alındı" : "Kısmen geri alındı"}</span>}
            {b.active > 0 && <Button size="sm" variant="ghost" className="h-6 text-rose-600" onClick={(e) => { e.preventDefault(); undo(b); }} data-testid={`party-batch-rollback-${b.id}`}><RotateCcw className="h-3 w-3 mr-1" />Tümünü geri al</Button>}
          </summary>
          <div className="pl-4 mt-1 space-y-0.5">{b.customers.map((c) => <div key={c.id} className="flex items-center gap-2">
            <span className="font-mono">{c.code}</span><span className="truncate max-w-[260px]">{c.title}</span>
            {c.reverted ? <span className="text-slate-400">geri alındı</span> : <button className="text-rose-600 underline" onClick={() => undo(b, [c.id])} data-testid={`party-unmark-${c.code}`}>İşareti kaldır</button>}
          </div>)}</div>
        </details>
      ))}
    </div>
  );
}

export default function CreditBalanceView() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [type, setType] = useState("tedarikci");
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const load = useCallback(() => api.get("/party-marks/credit-balances", { params: { q } }).then(({ data }) => setRes(data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reload]);
  const all = res && res.items.length > 0 && res.items.every((i) => sel.has(i.id));
  const toggle = (id) => { const s = new Set(sel); s.has(id) ? s.delete(id) : s.add(id); setSel(s); };
  const preview = async () => { try { setPrev((await api.post("/party-marks/preview", { ids: [...sel], party_type: type })).data); } catch (e) { toast.error(apiError(e)); } };
  const apply = async () => {
    setBusy(true);
    try { const { data } = await api.post("/party-marks/apply", { ids: [...sel], party_type: type }); toast.success(`${data.count} cari işaretlendi (${data.id})`); setPrev(null); setSel(new Set()); setReload((r) => r + 1); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="credit-balance-view">
      <div className="panel overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-200">
          <Input className="h-9 max-w-xs" placeholder="Unvan veya kod ara…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="credit-search-input" />
          <span className="text-[12px] text-slate-600" data-testid="credit-summary">{res ? `${res.count} alacak bakiyeli cari · toplam ${formatTRY(res.total)}` : "…"}</span>
          <div className="flex-1" />
          <span className="text-[12px] text-slate-600">{sel.size} seçili</span>
          <select className="h-9 border rounded px-2 text-[13px]" value={type} onChange={(e) => setType(e.target.value)} data-testid="credit-party-type">
            <option value="tedarikci">Tedarikçi</option><option value="her_ikisi">Her ikisi</option></select>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={!sel.size} onClick={preview} data-testid="credit-mark-button"><Truck className="h-4 w-4 mr-1" />Seçilileri {type === "tedarikci" ? "Tedarikçi" : "Her ikisi"} işaretle</Button>
        </div>
        <div className="overflow-x-auto max-h-[calc(100vh-300px)]">
          <table className="erp-table" data-testid="credit-table">
            <thead><tr><th><input type="checkbox" checked={!!all} onChange={() => setSel(all ? new Set() : new Set(res.items.map((i) => i.id)))} data-testid="credit-select-all" title="Tümünü seç" /></th>
              <th>Kod</th><th>Unvan</th><th className="text-right">Alacak Bakiyesi</th><th>Son Tahsilat</th><th className="text-right">Ciro</th><th>Tip</th></tr></thead>
            <tbody>{!res ? null : res.items.length === 0 ? <tr><td colSpan={7} className="text-slate-400">Alacak bakiyeli cari yok</td></tr> : res.items.map((c) => (
              <tr key={c.id} data-testid={`credit-row-${c.code}`} className={sel.has(c.id) ? "bg-blue-50" : ""}>
                <td><input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} data-testid={`credit-check-${c.code}`} /></td>
                <td className="font-mono text-xs">{c.code}</td><td className="max-w-[280px] truncate font-medium">{c.title}</td>
                <td className="text-right font-mono font-semibold text-emerald-700">{formatTRY(c.alacak)}</td><td className="text-xs">{c.son_tahsilat || "—"}</td>
                <td className="text-right font-mono">{c.ciro != null ? formatTRY(c.ciro) : "—"}</td><td className="text-xs">{PT[c.party_type]}</td></tr>))}</tbody>
          </table>
        </div>
      </div>
      <History reload={reload} onChanged={() => setReload((r) => r + 1)} />
      {prev && <PreviewDialog p={prev} type={type} busy={busy} onClose={() => setPrev(null)} onApply={apply} />}
    </div>
  );
}
